import { fetchData } from 'Utils/fetch-data';
import type { HistoryCoursesReportFilterI } from 'types';

export type ReportJobStatusT = 'pending' | 'processing' | 'done' | 'error';

export interface ReportJobI {
  id: string;
  type: string;
  status: ReportJobStatusT;
  fileName: string;
  /** Avance de 0 a 100. */
  progress: number;
  totalUnits: number;
  processedUnits: number;
  errorMessage: string | null;
  createdAt: string;
  finishedAt: string | null;
}

/**
 * La descarga viaja por el servidor y puede pesar varios MB. El tiempo límite
 * global de axios (10 s) es razonable para una consulta, pero corto para
 * transferir un archivo por una conexión lenta.
 */
const DOWNLOAD_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Reportes que se generan en segundo plano.
 *
 * A diferencia del resto de exportaciones, aquí el navegador no espera a que
 * el archivo exista: pide que se genere, y después consulta cómo va.
 */
class ReportJobsApi {
  endpoint: string;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  /**
   * Solicita la generación del reporte masivo de cursos.
   * POST /courses/report/jobs
   *
   * Responde de inmediato. Si el usuario ya tiene corriendo este mismo
   * reporte, el backend devuelve ese en lugar de arrancar otro.
   */
  async createCoursesReportJob(courseIds: string[], filters: HistoryCoursesReportFilterI = {}) {
    const url = `${this.endpoint}/courses/report/jobs`;
    const response = await fetchData<ReportJobI>(url, {
      method: 'POST',
      data: { ...filters, courseIds },
      // `meta.alreadyRunning` indica que el backend devolvió un reporte que ya
      // estaba en marcha en vez de arrancar uno nuevo.
      includeJsonApiMeta: true,
    });
    return response;
  }

  /**
   * Reportes en proceso y los que terminaron hace poco.
   * GET /courses/report/jobs
   */
  async getCoursesReportJobs() {
    const url = `${this.endpoint}/courses/report/jobs`;
    const response = await fetchData<ReportJobI[]>(url, { method: 'GET' });
    return response;
  }

  /**
   * Descarga el Excel de un reporte terminado.
   * GET /courses/report/jobs/:id/download
   */
  async downloadCoursesReportJob(jobId: string) {
    const url = `${this.endpoint}/courses/report/jobs/${jobId}/download`;
    const response = await fetchData<Blob>(url, {
      method: 'GET',
      responseType: 'blob',
      timeout: DOWNLOAD_TIMEOUT_MS,
    });
    return response;
  }
}

const reportJobsApi = new ReportJobsApi('/user-course-history');

export default reportJobsApi;
